import { useFormik } from 'formik'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { UPDATE_BASIC_SETTINGS } from '../../constants/career-history'
import trackEvents from '../../constants/track-events'
import {
  useNotificationContext,
  usePartnerContext,
  usePaymentContext
} from '../../context'
import { trackEvent } from '../../helpers/analytics'
import {
  getExpiresAfterDays,
  readExpiresAfterDays
} from '../../helpers/assessment-deadline'
import { useCreateLog } from '../../hooks/use-create-log'
import Alert from '../alert'
import SelectMenu from '../select-menu'
import Button from '../tailwind/Button'
import CareerSettingsContainer from './career-settings-container'

const TRANSLATION_LOCATION = 'career.assessmentsettings.expires_after_days.'
const SETTINGS_LOCATION = 'settings.assessment.'

// Erste Option im Menü. Die Stelle hat dann keinen eigenen Wert, gespeichert
// wird null und es gilt die Einstellung des Partners (getExpiresAfterDays).
const INHERIT = 'inherit'

// Wie in den Assessment-Einstellungen (settings/assessment.js)
const DAY_OPTIONS = Array.from({ length: 30 }, (_, index) => index + 1)

// Gleicher Default wie in den Automatisierungen und in inviteTalent
const DEFAULT_REMINDER_DAYS = 7

const ExpiresAfterDaysSettings = ({ career, updateHandler }) => {
  const { t } = useTranslation()
  const { partner } = usePartnerContext()
  const { automations: automationsProductAvailable } = usePaymentContext()
  const { success, error } = useNotificationContext()
  const { createCareerHistoryLog } = useCreateLog()
  const navigate = useNavigate()

  const partnerDays = getExpiresAfterDays(partner.app_settings)
  const careerDays = readExpiresAfterDays(career.app_settings)

  const formatDays = (number) =>
    t(
      `${SETTINGS_LOCATION}assessment_expires_after_days_${
        number === 1 ? 'day' : 'days'
      }`,
      { number }
    )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: { days: careerDays || INHERIT },
    onSubmit: ({ days }) => {
      const assessment_expires_after_days = days === INHERIT ? null : days

      updateHandler({
        input: {
          id: career.id,
          app_settings: {
            ...career.app_settings,
            assessment_expires_after_days
          }
        },
        onSuccess: () => {
          createCareerHistoryLog({
            career_id: career.id,
            context: UPDATE_BASIC_SETTINGS,
            input: { assessment_expires_after_days }
          })
            .then(() => {
              success(t('career.flex.update_career_success'))
              trackEvent(trackEvents.UPDATE_CAREER_ASSESSMENT_SETTINGS)
            })
            .finally(() => formik.setSubmitting(false))
        },
        onError: () => {
          error()
          formik.setSubmitting(false)
        }
      })
    }
  })

  const selectedDays = formik.values.days
  const isInherited = selectedDays === INHERIT

  // Wie in den Assessment-Einstellungen muss die automatische Erinnerung vor
  // dem Fristende kommen, sonst verwirft cronTTLWorker sie. Einen geerbten Wert
  // prüft die Seite der Assessment-Einstellungen.
  const {
    automation_assessment_reminder_active: reminderActive,
    automation_assessment_reminder_days: reminderDays
  } = JSON.parse(partner.app_settings?.automations || '{}')
  const conflict =
    !isInherited &&
    !!automationsProductAvailable &&
    !!reminderActive &&
    selectedDays <= (reminderDays || DEFAULT_REMINDER_DAYS)

  // Ein eigener Wert über 30 Tage (z. B. per API gesetzt) bleibt wählbar,
  // sonst zeigte das Menü still die erste Option
  const dayOptions = DAY_OPTIONS.includes(careerDays)
    ? DAY_OPTIONS
    : [...DAY_OPTIONS, careerDays].filter(Boolean)

  return (
    <CareerSettingsContainer
      title={t(TRANSLATION_LOCATION + 'title')}
      description={t(TRANSLATION_LOCATION + 'description')}
      badge='CAREER_EXPIRES_AFTER_DAYS'
      dirty={conflict ? false : formik.dirty}
      onSubmit={formik.handleSubmit}
      isLoading={formik.isSubmitting}
    >
      <SelectMenu
        id='career_assessment_expires_after_days'
        label={t(SETTINGS_LOCATION + 'assessment_expires_after_days_title')}
        description={t(
          TRANSLATION_LOCATION +
            (isInherited ? 'inherited_hint' : 'individual_hint'),
          { days: formatDays(partnerDays) }
        )}
        options={[
          [
            INHERIT,
            t(TRANSLATION_LOCATION + 'inherit_option', {
              days: formatDays(partnerDays)
            })
          ],
          ...dayOptions.map((days) => [days, formatDays(days)])
        ]}
        value={selectedDays}
        onChange={(days) => formik.setFieldValue('days', days)}
        disabled={formik.isSubmitting}
        className='w-full sm:w-2/3'
        translate={false}
      />
      {conflict && (
        <div className='max-w-lg'>
          <Alert
            type='error'
            title={t(SETTINGS_LOCATION + 'conflict_title')}
            description={t(SETTINGS_LOCATION + 'conflict_description')}
          />
        </div>
      )}
      <Button.Text
        text={t(TRANSLATION_LOCATION + 'settings_link')}
        onClick={() => navigate('/settings/assessment')}
      />
    </CareerSettingsContainer>
  )
}

ExpiresAfterDaysSettings.propTypes = {
  career: PropTypes.object.isRequired,
  updateHandler: PropTypes.func.isRequired
}

export default ExpiresAfterDaysSettings
